import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import classNames from 'classnames';

// ダイアログの選択結果（キャメルケースで統一）
export type GuardDecision =
  | 'goProfile'      // 資料入力へ移動
  | 'continue'       // 今回は続行
  | 'snoozeSession'  // 今回ログイン中は表示しない
  | 'snooze1d'       // 本日中は表示しない（24h）
  | 'cancel';        // 操作を中断

// スヌーズ用キー（セッション/1日）
export const SESSION_SNOOZE_KEY = 'mypage_profile_guard_snooze_session';
export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// いまスヌーズ中かを判定（セッション or 期限内）
export const isProfileGuardSnoozed = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (sessionStorage.getItem(SESSION_SNOOZE_KEY) === '1') return true;
  const until = Number(localStorage.getItem('mypage_profile_nudge_snooze_until_ms') || '0');
  return Date.now() < until;
};

// セッション中のみ非表示
export const markSnoozeSession = () => {
  sessionStorage.setItem(SESSION_SNOOZE_KEY, '1');
};

// 24時間非表示
export const markSnooze1Day = () => {
  localStorage.setItem('mypage_profile_nudge_snooze_until_ms', String(Date.now() + ONE_DAY_MS));
};

// 任意時間（時間単位）のスヌーズを付与したい場合のヘルパ（必要なら親側で使用可）
export const markSnoozeHours = (hours: number) => {
  const ms = Math.max(0, Math.floor(hours)) * 60 * 60 * 1000;
  localStorage.setItem('mypage_profile_nudge_snooze_until_ms', String(Date.now() + ms));
};

type Props = {
  open: boolean;
  onClose: (decision: GuardDecision) => void;
  // 足りない項目の簡易表示（任意）
  missingFields?: string[];
  // i18n の名前空間キー（例: 'mypage'）。未指定なら 'mypage'
  i18nNsKey?: string;
  // UI 文言用のスヌーズ時間（時間）。実際の保存は親側で実行でもOK
  snoozeHours?: number;
  // ★ NEW: 强制风险确认勾选（诊疗入口专用）
  requireRiskCheck?: boolean;
};

// ★ ここを "ProfileGuardDialog" という名前で宣言
const ProfileGuardDialog: React.FC<Props> = ({
  open,
  onClose,
  missingFields,
  i18nNsKey = 'mypage',
  snoozeHours = 24,
  requireRiskCheck = false,
}) => {
  const { t } = useTranslation();
  const [riskAccepted, setRiskAccepted] = useState(false);

  if (!open) return null;

  const k = (suffix: string) => `${i18nNsKey}.${suffix}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 背景クリックでキャンセル */}
      <button
        type="button"
        className="absolute inset-0 bg-black/20"
        onClick={() => onClose('cancel')}
        aria-label="Close"
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-[92%] p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t(k('profile_guard_title')) || '基本情報の入力がまだのようです'}
        </h3>

        <p className="text-sm text-gray-700 mb-3">
          {t(k('profile_guard_desc')) || '問診票の前に「氏名・郵便番号・住所 など」の基本情報をご入力ください。'}
        </p>

        {/* 不足項目の表示（任意） */}
        {Array.isArray(missingFields) && missingFields.length > 0 && (
          <div className="mb-3 text-xs text-gray-600">
            <div className="font-semibold mb-1">
              {t(k('profile_guard_missing')) || '未入力と思われる項目'}
            </div>
            <ul className="list-disc list-inside space-y-0.5">
              {missingFields.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-xl shadow-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-[13px] text-red-700 font-bold leading-relaxed">
              {t(k('profile_guard_risk_high'))}
            </p>
          </div>
        </div>

        {requireRiskCheck && (
          <div className="mb-5 px-1 py-0.5 animate-in fade-in slide-in-from-top-1">
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex items-center mt-0.5">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer transition-all"
                  checked={riskAccepted}
                  onChange={(e) => setRiskAccepted(e.target.checked)}
                />
              </div>
              <span className="text-[12px] text-gray-600 leading-relaxed font-medium group-hover:text-gray-900 transition-colors">
                {t(k('profile_guard_risk_checkbox'))}
              </span>
            </label>
          </div>
        )}

        <div className="space-y-3">
          <button
            className="w-full h-12 flex items-center justify-center rounded-xl bg-gradient-primary text-white font-bold shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98]"
            onClick={() => onClose('goProfile')}
          >
            {t(k('profile_guard_go_profile')) || '今すぐ基本情報を入力する'}
          </button>

          <button
            className={classNames(
              "w-full h-11 flex items-center justify-center rounded-xl border font-medium transition-all active:scale-[0.98]",
              requireRiskCheck && !riskAccepted
                ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
                : "border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm"
            )}
            onClick={() => {
              if (requireRiskCheck && !riskAccepted) return;
              onClose('continue');
            }}
            disabled={requireRiskCheck && !riskAccepted}
          >
            {t(k('profile_guard_continue')) || 'このまま次へ進む'}
          </button>

          <div className="flex flex-col items-center gap-2 pt-2">
            <button
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
              onClick={() => onClose('snooze1d')}
            >
              {t(k('profile_guard_snooze_1d')) || `今日はもう表示しない（${snoozeHours}時間）`}
            </button>
            <button
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => onClose('cancel')}
            >
              {t('common.cancel') || 'キャンセル'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProfileGuardDialog;
